import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Lock } from "lucide-react";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Field } from "@/components/common/States";
import { useSession } from "@/context/SessionContext";
import { errMsg, errCode } from "@/lib/format";
import { clearLookups } from "@/lib/lookups";
import { FieldInput, getPath, setPath } from "./FieldInput";

const isEmpty = (v) => v === null || v === undefined || v === "" || (Array.isArray(v) && !v.length);

function prune(v) {
  if (Array.isArray(v)) return v;
  if (v && typeof v === "object") {
    const out = Object.fromEntries(Object.entries(v).map(([k, x]) => [k, prune(x)]));
    return Object.values(out).every((x) => isEmpty(x) || x === false) ? null : out;
  }
  return v === "" ? null : v;
}

export function buildBody(cfg, values, isEdit, can) {
  let body = {};
  cfg.sections.forEach((s) => {
    if (s.perm && !can(s.perm)) return;
    s.fields.forEach((f) => {
      if (f.viewOnly || (isEdit && f.createOnly)) return;
      const hidden = (s.show && !s.show(values)) || (f.show && !f.show(values));
      let v = hidden ? null : getPath(values, f.name);
      if (f.type === "tags") v = (v || []).map((x) => x.trim()).filter(Boolean);
      if (f.type === "contacts") v = (v || []).filter((c) => c.name && c.name.trim());
      body = setPath(body, f.name, v === undefined ? null : v);
    });
  });
  return Object.fromEntries(Object.entries(body).map(([k, v]) => [k, typeof v === "object" && !Array.isArray(v) ? prune(v) : v]));
}

const Section = ({ s, values, setValues, isEdit, can }) => {
  if (s.show && !s.show(values)) return null;
  const locked = s.perm && !can(s.perm);
  const fields = s.fields.filter((f) => !f.viewOnly && (!f.show || f.show(values)) && !(isEdit && f.createOnly));
  if (!fields.length) return null;
  return (
    <fieldset className="border-t pt-4" data-testid={`form-section-${s.title.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
      <legend className="font-display font-semibold text-sm pr-2 flex items-center gap-2">{s.title}{locked && <Lock className="h-3.5 w-3.5 text-gold" />}</legend>
      {locked ? <p className="text-xs text-muted-foreground mt-2">You need <span className="font-mono-g">{s.perm}</span> to edit this section.</p> : (
        <div className="grid sm:grid-cols-2 gap-4 mt-3">
          {s.note && !isEdit && <p className="sm:col-span-2 text-xs text-muted-foreground">{s.note}</p>}
          {fields.map((f) => (
            <Field key={f.name} label={f.label ? `${f.label}${f.required ? " *" : ""}` : ""} hint={f.hint} className={f.span === 2 ? "sm:col-span-2" : ""}>
              <FieldInput field={f} value={getPath(values, f.name)} onChange={(v) => setValues((p) => setPath(p, f.name, v))} />
            </Field>
          ))}
        </div>
      )}
    </fieldset>
  );
};

export const EntityForm = ({ cfg, open, onOpenChange, record, onSaved }) => {
  const { api, can } = useSession();
  const [values, setValues] = useState({});
  const [saving, setSaving] = useState(false);
  const [dups, setDups] = useState(null);
  const isEdit = !!record;

  useEffect(() => {
    if (open) setValues(record ? JSON.parse(JSON.stringify(record)) : { ...cfg.defaults });
  }, [open, record, cfg]);

  const submit = async (allowDuplicate = false) => {
    const missing = cfg.sections.flatMap((s) => (s.show && !s.show(values)) || (s.perm && !can(s.perm)) ? [] : s.fields)
      .filter((f) => f.required && !f.viewOnly && !(isEdit && f.createOnly) && (!f.show || f.show(values)) && isEmpty(getPath(values, f.name)));
    if (missing.length) return toast.error(`Required: ${missing.map((f) => f.label).join(", ")}`);
    setSaving(true);
    const body = buildBody(cfg, values, isEdit, can);
    if (allowDuplicate) body.allow_duplicate = true;
    try {
      let data;
      if (isEdit) ({ data } = await api.patch(`/${cfg.kind}/${record.id}`, { ...body, version: record.version }));
      else ({ data } = await api.post(`/${cfg.kind}`, body));
      clearLookups();
      toast.success(`${cfg.label} ${isEdit ? "updated" : "created"}`);
      onSaved?.(data);
      onOpenChange(false);
    } catch (e) {
      if (errCode(e) === "possible_duplicate") setDups(e.response.data.detail.matches);
      else toast.error(errMsg(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto" data-testid={`${cfg.entity}-form-dialog`}>
          <DialogHeader>
            <DialogTitle>{isEdit ? `Edit ${cfg.label.toLowerCase()}` : `New ${cfg.label.toLowerCase()}`}</DialogTitle>
            <DialogDescription>Fields marked * are required. All changes are audited.</DialogDescription>
          </DialogHeader>
          <form onSubmit={(e) => { e.preventDefault(); submit(); }} className="space-y-5">
            {cfg.sections.map((s) => <Section key={s.title} s={s} values={values} setValues={setValues} isEdit={isEdit} can={can} />)}
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
              <Button type="submit" disabled={saving} data-testid={`${cfg.entity}-save-button`}>{saving ? "Saving…" : `Save ${cfg.label.toLowerCase()}`}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
      <AlertDialog open={!!dups} onOpenChange={(o) => !o && setDups(null)}>
        <AlertDialogContent data-testid="duplicate-warning-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Possible duplicate {cfg.label.toLowerCase()}</AlertDialogTitle>
            <AlertDialogDescription>These records share the same phone or email:</AlertDialogDescription>
          </AlertDialogHeader>
          <ul className="text-sm space-y-1">{(dups || []).map((d) => <li key={d.id}>• {d.name} <span className="text-muted-foreground">{d.code} · {d.phone} {d.email || ""}</span></li>)}</ul>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="duplicate-cancel-button">Go back</AlertDialogCancel>
            <AlertDialogAction onClick={() => { setDups(null); submit(true); }} data-testid="duplicate-confirm-button">Save anyway</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};
